import type { IBrandOpsContractClarification, IBrandOpsState } from './types';
import { types } from './types';

export const initialState: IBrandOpsState = {
    homeStats: null,
    fetchingHomeStats: false,
    access: null,
    fetchingAccess: false,
    team: [],
    fetchingTeam: false,
    inviting: false,
    contracts: [],
    fetchingContracts: false,
    activeContract: null,
    fetchingContract: false,
    mutatingContract: false,
    calendar: [],
    fetchingCalendar: false,
    mutatingCalendar: false,
    settings: null,
    fetchingSettings: false,
    knowledge: [],
    fetchingKnowledge: false,
    mutatingKnowledge: false,
    aiResult: null,
    aiBusy: false,
    inbox: [],
    fetchingInbox: false,
    inboxStatus: null,
    conversations: [],
    fetchingConversations: false,
    conversationMessages: [],
    fetchingMessages: false,
    sendingMessage: false,
    ugcAssets: [],
    fetchingUgcAssets: false,
    mutatingEscrow: false,
    downloadingPdf: false,
    error: null,
    apiUnavailable: false,
    fetchingMyMemberships: false,
    myMembershipsFetched: false,
    myContract: null,
    fetchingMyContract: false,
    automations: [],
    fetchingAutomations: false,
    mutatingAutomation: false,
    clarifications: [],
    fetchingClarifications: false,
    replyingToClarification: false,
    pendingClarificationCount: 0,
    pendingContractSignedCount: 0,
    pendingContractDeclinedCount: 0,
    pendingNewDmCount: 0,
    lastNewDm: null,
};

function isUnavailable(err: any): boolean {
    const status = err?.status || err?.response?.status || err?.data?.status;
    return status === 404 || status === 501;
}

export function brandOpsReducer(state = initialState, action: any): IBrandOpsState {
    // Never let redux-persist rehydration overwrite brandOps — all data is fetched
    // fresh on each session. Without this guard, REHYDRATE can fire after sagas
    // have already populated the state and reset it to stale persisted values.
    if (action.type === 'persist/REHYDRATE') return state;

    switch (action.type) {
        case types.FETCH_HOME_STATS:
            return { ...state, fetchingHomeStats: true };
        case types.FETCH_HOME_STATS_SUCCESS:
            return { ...state, fetchingHomeStats: false, homeStats: action.payload };

        case types.FETCH_ACCESS:
            return { ...state, fetchingAccess: true, error: null };
        case types.FETCH_ACCESS_SUCCESS:
            return { ...state, fetchingAccess: false, access: action.payload, apiUnavailable: false };
        case types.FETCH_ACCESS_FAIL:
            return {
                ...state,
                fetchingAccess: false,
                error: action.payload,
                apiUnavailable: isUnavailable(action.payload),
                access: isUnavailable(action.payload)
                    ? {
                          can_access: false,
                          has_content_manager: false,
                          message: 'Brand Manage is not available yet for this workspace.',
                      }
                    : state.access,
            };

        case types.FETCH_TEAM:
            return { ...state, fetchingTeam: true, error: null };
        case types.FETCH_TEAM_SUCCESS:
            return { ...state, fetchingTeam: false, team: action.payload || [], apiUnavailable: false };
        case types.FETCH_TEAM_FAIL:
            return {
                ...state,
                fetchingTeam: false,
                error: action.payload,
                team: isUnavailable(action.payload) ? [] : state.team,
                apiUnavailable: isUnavailable(action.payload) || state.apiUnavailable,
            };

        case types.INVITE_TEAM:
            return { ...state, inviting: true, error: null };
        case types.INVITE_TEAM_SUCCESS:
            return {
                ...state,
                inviting: false,
                team: [action.payload, ...state.team.filter((m: any) => m._id !== action.payload?._id)],
            };
        case types.INVITE_TEAM_FAIL:
            return { ...state, inviting: false, error: action.payload };

        case types.UPDATE_TEAM_MEMBER_SUCCESS:
            return {
                ...state,
                team: state.team.map(m => (m._id === action.payload?._id ? action.payload : m)),
            };
        case types.REMOVE_TEAM_MEMBER_SUCCESS:
            return { ...state, team: state.team.filter(m => m._id !== action.payload) };

        case types.FETCH_CONTRACTS:
            return { ...state, fetchingContracts: true, error: null };
        case types.FETCH_CONTRACTS_SUCCESS:
            return { ...state, fetchingContracts: false, contracts: action.payload || [] };
        case types.FETCH_CONTRACTS_FAIL:
            return {
                ...state,
                fetchingContracts: false,
                error: action.payload,
                contracts: isUnavailable(action.payload) ? [] : state.contracts,
                apiUnavailable: isUnavailable(action.payload) || state.apiUnavailable,
            };

        case types.CREATE_CONTRACT:
        case types.DISPATCH_CONTRACT:
        case types.SIGN_CONTRACT:
        case types.APPROVE_CONTRACT:
            return { ...state, mutatingContract: true, error: null };
        case types.CREATE_CONTRACT_SUCCESS:
        case types.DISPATCH_CONTRACT_SUCCESS:
        case types.SIGN_CONTRACT_SUCCESS:
        case types.APPROVE_CONTRACT_SUCCESS:
            return {
                ...state,
                mutatingContract: false,
                activeContract: action.payload,
                contracts: [action.payload, ...state.contracts.filter(c => c._id !== action.payload?._id)],
            };
        case types.CREATE_CONTRACT_FAIL:
        case types.DISPATCH_CONTRACT_FAIL:
        case types.SIGN_CONTRACT_FAIL:
        case types.APPROVE_CONTRACT_FAIL:
            return { ...state, mutatingContract: false, error: action.payload };

        case types.GET_CONTRACT:
            return { ...state, fetchingContract: true, error: null };
        case types.GET_CONTRACT_SUCCESS:
            return { ...state, fetchingContract: false, activeContract: action.payload };
        case types.GET_CONTRACT_FAIL:
            return { ...state, fetchingContract: false, error: action.payload, activeContract: null };

        case types.FETCH_CALENDAR:
            return { ...state, fetchingCalendar: true, error: null };
        case types.FETCH_CALENDAR_SUCCESS:
            return { ...state, fetchingCalendar: false, calendar: action.payload || [] };
        case types.FETCH_CALENDAR_FAIL:
            return {
                ...state,
                fetchingCalendar: false,
                error: action.payload,
                calendar: isUnavailable(action.payload) ? [] : state.calendar,
                apiUnavailable: isUnavailable(action.payload) || state.apiUnavailable,
            };

        case types.CREATE_CALENDAR_ITEM:
        case types.UPDATE_CALENDAR_ITEM:
        case types.APPROVE_CALENDAR_ITEM:
        case types.PUBLISH_CALENDAR_ITEM:
            return { ...state, mutatingCalendar: true, error: null };
        case types.CREATE_CALENDAR_ITEM_SUCCESS:
            return {
                ...state,
                mutatingCalendar: false,
                calendar: [action.payload, ...state.calendar.filter(i => i._id !== action.payload?._id)],
            };
        case types.UPDATE_CALENDAR_ITEM_SUCCESS:
        case types.APPROVE_CALENDAR_ITEM_SUCCESS:
        case types.PUBLISH_CALENDAR_ITEM_SUCCESS:
            return {
                ...state,
                mutatingCalendar: false,
                calendar: state.calendar.map(i => (i._id === action.payload?._id ? action.payload : i)),
            };
        case types.CREATE_CALENDAR_ITEM_FAIL:
        case types.UPDATE_CALENDAR_ITEM_FAIL:
        case types.APPROVE_CALENDAR_ITEM_FAIL:
        case types.PUBLISH_CALENDAR_ITEM_FAIL:
            return { ...state, mutatingCalendar: false, error: action.payload };

        case types.FETCH_SETTINGS:
            return { ...state, fetchingSettings: true, error: null };
        case types.FETCH_SETTINGS_SUCCESS:
            return { ...state, fetchingSettings: false, settings: action.payload };
        case types.FETCH_SETTINGS_FAIL:
            return { ...state, fetchingSettings: false, error: action.payload };
        case types.UPDATE_SETTINGS_SUCCESS:
            return { ...state, settings: action.payload };

        case types.DOWNLOAD_CONTRACT_PDF:
            return { ...state, downloadingPdf: true, error: null };
        case types.DOWNLOAD_CONTRACT_PDF_SUCCESS:
            return { ...state, downloadingPdf: false };
        case types.DOWNLOAD_CONTRACT_PDF_FAIL:
            return { ...state, downloadingPdf: false, error: action.payload };

        case types.FETCH_KNOWLEDGE:
            return { ...state, fetchingKnowledge: true, error: null };
        case types.FETCH_KNOWLEDGE_SUCCESS:
            return { ...state, fetchingKnowledge: false, knowledge: action.payload || [] };
        case types.FETCH_KNOWLEDGE_FAIL:
            return {
                ...state,
                fetchingKnowledge: false,
                error: action.payload,
                knowledge: isUnavailable(action.payload) ? [] : state.knowledge,
            };

        case types.ADD_KNOWLEDGE:
        case types.REMOVE_KNOWLEDGE:
            return { ...state, mutatingKnowledge: true, error: null };
        case types.ADD_KNOWLEDGE_SUCCESS:
            return {
                ...state,
                mutatingKnowledge: false,
                knowledge: [action.payload, ...state.knowledge.filter(d => d._id !== action.payload?._id)],
            };
        case types.REMOVE_KNOWLEDGE_SUCCESS:
            return {
                ...state,
                mutatingKnowledge: false,
                knowledge: state.knowledge.filter(d => d._id !== action.payload),
            };
        case types.ADD_KNOWLEDGE_FAIL:
        case types.REMOVE_KNOWLEDGE_FAIL:
            return { ...state, mutatingKnowledge: false, error: action.payload };

        case types.AI_PLAYGROUND:
            return { ...state, aiBusy: true, error: null };
        case types.AI_PLAYGROUND_SUCCESS:
            return { ...state, aiBusy: false, aiResult: action.payload };
        case types.AI_PLAYGROUND_FAIL:
            return { ...state, aiBusy: false, error: action.payload };

        case types.FETCH_INBOX:
            return { ...state, fetchingInbox: true, error: null };
        case types.FETCH_INBOX_SUCCESS:
            return { ...state, fetchingInbox: false, inbox: action.payload || [] };
        case types.FETCH_INBOX_FAIL:
            return {
                ...state,
                fetchingInbox: false,
                error: action.payload,
                inbox: isUnavailable(action.payload) ? [] : state.inbox,
            };

        case types.FETCH_INBOX_STATUS_SUCCESS:
            return { ...state, inboxStatus: action.payload || null };
        case types.FETCH_INBOX_STATUS_FAIL:
            return {
                ...state,
                inboxStatus: { connected: false, messaging_ready: false, reason: 'status_unavailable' },
            };

        case types.LOCK_ESCROW:
        case types.PAYOUT_ESCROW:
        case types.RELEASE_ESCROW:
        case types.ADD_STRIKE:
            return { ...state, mutatingEscrow: true, error: null };
        case types.LOCK_ESCROW_SUCCESS:
        case types.PAYOUT_ESCROW_SUCCESS:
        case types.RELEASE_ESCROW_SUCCESS:
        case types.ADD_STRIKE_SUCCESS:
            return {
                ...state,
                mutatingEscrow: false,
                activeContract: action.payload || state.activeContract,
                contracts: action.payload
                    ? [action.payload, ...state.contracts.filter(c => c._id !== action.payload?._id)]
                    : state.contracts,
            };
        case types.LOCK_ESCROW_FAIL:
        case types.PAYOUT_ESCROW_FAIL:
        case types.RELEASE_ESCROW_FAIL:
        case types.ADD_STRIKE_FAIL:
            return { ...state, mutatingEscrow: false, error: action.payload };

        case types.FETCH_CONVERSATIONS:
            return { ...state, fetchingConversations: true, error: null };
        case types.FETCH_CONVERSATIONS_SUCCESS:
            return { ...state, fetchingConversations: false, conversations: action.payload || [] };
        case types.FETCH_CONVERSATIONS_FAIL:
            return {
                ...state,
                fetchingConversations: false,
                error: action.payload,
                conversations: isUnavailable(action.payload) ? [] : state.conversations,
            };

        case types.FETCH_CONVERSATION_MESSAGES:
            return { ...state, fetchingMessages: true, error: null };
        case types.FETCH_CONVERSATION_MESSAGES_SUCCESS:
            return { ...state, fetchingMessages: false, conversationMessages: action.payload || [] };
        case types.FETCH_CONVERSATION_MESSAGES_FAIL:
            return { ...state, fetchingMessages: false, error: action.payload, conversationMessages: [] };

        case types.SEND_INBOX_MESSAGE:
            return { ...state, sendingMessage: true, error: null };
        case types.SEND_INBOX_MESSAGE_SUCCESS:
            return { ...state, sendingMessage: false };
        case types.SEND_INBOX_MESSAGE_FAIL:
            return { ...state, sendingMessage: false, error: action.payload };

        case types.FETCH_UGC_ASSETS:
            return { ...state, fetchingUgcAssets: true, error: null };
        case types.FETCH_UGC_ASSETS_SUCCESS:
            return { ...state, fetchingUgcAssets: false, ugcAssets: action.payload || [] };
        case types.FETCH_UGC_ASSETS_FAIL:
            return {
                ...state,
                fetchingUgcAssets: false,
                error: action.payload,
                ugcAssets: isUnavailable(action.payload) ? [] : state.ugcAssets,
            };

        case types.FETCH_MY_MEMBERSHIPS:
            return { ...state, fetchingMyMemberships: true };
        case types.FETCH_MY_MEMBERSHIPS_SUCCESS:
            return { ...state, fetchingMyMemberships: false, myMembershipsFetched: true };
        case types.FETCH_MY_MEMBERSHIPS_FAIL:
            return { ...state, fetchingMyMemberships: false, myMembershipsFetched: true };

        case types.FETCH_MY_CONTRACT:
            return { ...state, fetchingMyContract: true };
        case types.FETCH_MY_CONTRACT_SUCCESS:
            return { ...state, fetchingMyContract: false, myContract: action.payload ?? null };
        case types.FETCH_MY_CONTRACT_FAIL:
            return { ...state, fetchingMyContract: false };

        case types.RESEND_TEAM_INVITE:
            return { ...state, inviting: true, error: null };
        case types.RESEND_TEAM_INVITE_SUCCESS:
        case types.RESEND_TEAM_INVITE_FAIL:
            return { ...state, inviting: false };

        case types.FREEZE_CONTRACT:
        case types.VOID_CONTRACT:
            return { ...state, mutatingContract: true, error: null };
        case types.FREEZE_CONTRACT_SUCCESS:
        case types.VOID_CONTRACT_SUCCESS:
            return {
                ...state,
                mutatingContract: false,
                activeContract: action.payload,
                contracts: state.contracts.map(c => (c._id === action.payload?._id ? action.payload : c)),
            };
        case types.FREEZE_CONTRACT_FAIL:
        case types.VOID_CONTRACT_FAIL:
            return { ...state, mutatingContract: false, error: action.payload };

        case types.RENEW_CONTRACT:
            return { ...state, mutatingContract: true, error: null };
        case types.RENEW_CONTRACT_SUCCESS:
            return {
                ...state,
                mutatingContract: false,
                activeContract: action.payload,
                contracts: [action.payload, ...state.contracts],
            };
        case types.RENEW_CONTRACT_FAIL:
            return { ...state, mutatingContract: false, error: action.payload };

        case types.FETCH_AUTOMATIONS:
            return { ...state, fetchingAutomations: true, error: null };
        case types.FETCH_AUTOMATIONS_SUCCESS:
            return { ...state, fetchingAutomations: false, automations: action.payload || [] };
        case types.FETCH_AUTOMATIONS_FAIL:
            return { ...state, fetchingAutomations: false, error: action.payload };

        case types.UPSERT_AUTOMATION:
        case types.TOGGLE_AUTOMATION:
        case types.DELETE_AUTOMATION:
            return { ...state, mutatingAutomation: true, error: null };
        case types.UPSERT_AUTOMATION_SUCCESS:
        case types.TOGGLE_AUTOMATION_SUCCESS: {
            const updated = action.payload as any;
            const exists = state.automations.some(a => a.social_post_id === updated?.social_post_id);
            return {
                ...state,
                mutatingAutomation: false,
                automations: exists
                    ? state.automations.map(a => (a.social_post_id === updated?.social_post_id ? updated : a))
                    : [updated, ...state.automations],
            };
        }
        case types.DELETE_AUTOMATION_SUCCESS: {
            const { automationId } = action.payload as any;
            return {
                ...state,
                mutatingAutomation: false,
                automations: state.automations.filter(a => a.automation._id !== automationId),
            };
        }
        case types.UPSERT_AUTOMATION_FAIL:
        case types.TOGGLE_AUTOMATION_FAIL:
        case types.DELETE_AUTOMATION_FAIL:
            return { ...state, mutatingAutomation: false, error: action.payload };

        case types.FETCH_CONTRACT_CLARIFICATIONS:
            return { ...state, fetchingClarifications: true };
        case types.FETCH_CONTRACT_CLARIFICATIONS_SUCCESS:
            return { ...state, fetchingClarifications: false, clarifications: action.payload || [] };
        case types.FETCH_CONTRACT_CLARIFICATIONS_FAIL:
            return { ...state, fetchingClarifications: false };

        case types.REPLY_TO_CLARIFICATION:
            return { ...state, replyingToClarification: true };
        case types.REPLY_TO_CLARIFICATION_SUCCESS: {
            const updated = action.payload as IBrandOpsContractClarification;
            return {
                ...state,
                replyingToClarification: false,
                clarifications: state.clarifications.map(c => (c._id === updated._id ? updated : c)),
            };
        }
        case types.REPLY_TO_CLARIFICATION_FAIL:
            return { ...state, replyingToClarification: false };

        case types.BRAND_OPS_NEW_CLARIFICATION:
            return { ...state, pendingClarificationCount: state.pendingClarificationCount + 1 };

        case types.BRAND_OPS_CONTRACT_SIGNED:
            return { ...state, pendingContractSignedCount: state.pendingContractSignedCount + 1 };

        case types.BRAND_OPS_CONTRACT_DECLINED:
            return { ...state, pendingContractDeclinedCount: state.pendingContractDeclinedCount + 1 };

        case types.BRAND_OPS_NEW_DM:
            return {
                ...state,
                pendingNewDmCount: state.pendingNewDmCount + 1,
                lastNewDm: action.payload,
            };

        default:
            return state;
    }
}
